import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import './AuthForms.css';

export default function PasswordResetConfirm() {
  const { uid, token } = useParams();
  const { confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Debug: Comprehensive URL parameter validation
  useEffect(() => {
    console.group('📋 URL PARAMETERS - Initial Extraction & Validation');
    
    console.log('Raw URL Info:', {
      fullURL: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
    });
    
    console.log('Extracted Parameters:', {
      uid: uid,
      token: token,
    });
    
    console.log('Parameter Types:', {
      uid: typeof uid,
      token: typeof token,
    });
    
    console.log('Parameter Validation:', {
      uid: {
        exists: !!uid,
        length: uid?.length,
        isEmpty: uid?.length === 0,
        hasWhitespace: uid && uid !== uid.trim(),
        isValidFormat: uid && /^[a-zA-Z0-9_-]+$/.test(uid),
        preview: uid,
      },
      token: {
        exists: !!token,
        length: token?.length,
        isEmpty: token?.length === 0,
        hasWhitespace: token && token !== token.trim(),
        isValidFormat: token && /^[a-z0-9]+-[a-z0-9]+$/.test(token),
        preview: token?.substring(0, 30) + '...',
      }
    });
    
    // Warn if parameters are missing or malformed
    if (!uid || !token) {
      console.error('❌ CRITICAL: Missing URL parameters!', {
        missingUid: !uid,
        missingToken: !token,
      });
    }
    
    if (uid && /[^a-zA-Z0-9_-]/.test(uid)) {
      console.warn('⚠️ WARNING: UID contains unexpected characters');
    }
    
    if (token && /[^a-z0-9-]/.test(token)) {
      console.warn('⚠️ WARNING: Token contains unexpected characters');
    }
    
    console.groupEnd();
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    console.group('🔐 PASSWORD RESET SUBMISSION - Pre-Flight Checks');

    // Client-side validation
    console.log('Step 1: Client-side validation...');
    
    if (password !== password2) {
      console.error('❌ Validation failed: Passwords do not match');
      setError('Passwords do not match.');
      console.groupEnd();
      return;
    }

    if (password.length < 8) {
      console.error('❌ Validation failed: Password too short');
      setError('Password must be at least 8 characters long.');
      console.groupEnd();
      return;
    }

    console.log('✅ Client-side validation passed');
    
    // Pre-submission parameter check
    console.log('Step 2: Pre-submission parameter check...');
    console.table({
      'Parameter': ['uid', 'token', 'password1', 'password2'],
      'Exists': [!!uid, !!token, !!password, !!password2],
      'Type': [typeof uid, typeof token, typeof password, typeof password2],
      'Length': [uid?.length, token?.length, password?.length, password2?.length],
    });
    
    if (!uid || !token) {
      console.error('❌ CRITICAL: Missing required parameters');
      setError('Invalid password reset link. Please request a new one.');
      console.groupEnd();
      return;
    }
    
    console.log('Step 3: Calling confirmPasswordReset...');
    console.log('Parameters being passed:', {
      uid: uid,
      token: token?.substring(0, 20) + '...',
      passwordsProvided: true,
    });

    console.groupEnd();

    setLoading(true);
    try {
      await confirmPasswordReset(uid, token, password, password2);
      console.log('✅✅✅ PASSWORD RESET SUCCESSFUL!');
      setMessage('Your password has been reset successfully!');
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/subscribe');
      }, 2000);
    } catch (err) {
      console.group('❌ PASSWORD RESET FAILED - User-Facing Error Handler');
      
      // Error priority matches backend response format
      let errorMessage = 'Failed to reset password. The link may be invalid or expired.';
      let userFriendlyHelp = '';
      
      if (err?.response?.data) {
        const data = err.response.data;
        
        // Provide specific user-friendly messages based on error type
        if (data?.uid) {
          errorMessage = 'This password reset link is invalid or has expired.';
          userFriendlyHelp = 'The link may have already been used, or the user account may not exist. Please request a new password reset.';
        } else if (data?.token) {
          errorMessage = 'This password reset link has expired or is no longer valid.';
          userFriendlyHelp = 'Password reset links expire after a certain time for security. Please request a new password reset.';
        } else if (data?.new_password1) {
          errorMessage = data.new_password1[0] || 'Password does not meet requirements.';
          userFriendlyHelp = 'Please ensure your password is strong enough (typically 8+ characters, with mix of letters and numbers).';
        } else if (data?.new_password2) {
          errorMessage = data.new_password2[0] || 'Password confirmation error.';
        } else if (data?.detail) {
          errorMessage = data.detail;
        } else if (data?.non_field_errors) {
          errorMessage = data.non_field_errors[0] || 'An error occurred.';
        } else {
          errorMessage = JSON.stringify(data);
        }
        
        console.log('Backend Error Response:', data);
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      console.log('User will see:', errorMessage);
      if (userFriendlyHelp) {
        console.log('Help text:', userFriendlyHelp);
      }
      
      console.groupEnd();
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={handleSubmit}>
        <h2 className="auth-title">Set New Password</h2>
        {message && (
          <div className="auth-info" aria-live="polite">{message}</div>
        )}
        {error && (
          <div className="auth-error" aria-live="polite">{error}</div>
        )}

        <div className="auth-group">
          <label htmlFor="new-password">New Password</label>
          <input
            type="password"
            id="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Enter new password"
            disabled={loading || message}
            aria-disabled={loading || message}
            minLength={8}
          />
        </div>

        <div className="auth-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            type="password"
            id="confirm-password"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            required
            placeholder="Confirm new password"
            disabled={loading || message}
            aria-disabled={loading || message}
            minLength={8}
          />
        </div>

        <button 
          type="submit" 
          className="auth-button" 
          disabled={loading || message}
        >
          {loading ? 'Resetting…' : message ? 'Redirecting…' : 'Reset Password'}
        </button>

        <div className="auth-footer">
          <Link to="/forgot-password" className="auth-link">
            Request new reset link
          </Link>
          <span> | </span>
          <Link to="/subscribe" className="auth-link">Back to Login</Link>
        </div>
      </form>
    </div>
  );
}
